import React, { useState, useEffect, useCallback } from 'react';
import { usersAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Plus, Users, Edit2, X, Search, Shield, Wrench, Eye } from 'lucide-react';

import PermissionMatrix from '../components/users/PermissionMatrix';

const ROLE_COLORS = { admin:'#EF4444', supervisor:'#F59E0B', technician:'#10B981' };
const ROLE_ICONS = { admin:Shield, supervisor:Eye, technician:Wrench };
const avatarColors = ['#0EA5E9','#8B5CF6','#10B981','#F97316','#EF4444','#F59E0B'];
const getColor = (name) => avatarColors[name?.charCodeAt(0) % avatarColors.length] || '#0EA5E9';
const getInitials = (name) => name?.split(' ').map(p=>p[0]).join('').slice(0,2).toUpperCase() || '??';

function UserModal({ user, onClose, onSave }) {
  const [form, setForm] = useState({ name:user?.name||'', email:user?.email||'', role:user?.role||'technician', department:user?.department||'', employee_id:user?.employee_id||'', phone:user?.phone||'', password:'' });
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (!form.name || !form.email) return toast.error('Name and email required');
    if (!user && !form.password) return toast.error('Password required for new users');
    setLoading(true);
    try {
      if (user) await usersAPI.update(user.id, form);
      else await usersAPI.create(form);
      toast.success(user ? 'User updated!' : 'User created!');
      onSave();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-md">
        <div className="modal-header">
          <div className="modal-title">{user ? 'Edit Profile' : 'Create New User'}</div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="John Smith" />
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input className="form-input" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="john@company.com" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Role</label>
              <select className="form-select" value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>
                <option value="technician">Technician</option>
                <option value="supervisor">Supervisor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Department</label>
              <input className="form-input" value={form.department} onChange={e=>setForm(f=>({...f,department:e.target.value}))} placeholder="Maintenance, Electrical..." />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Employee ID</label>
              <input className="form-input" value={form.employee_id} onChange={e=>setForm(f=>({...f,employee_id:e.target.value}))} style={{ fontFamily:'var(--font-mono)' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} />
            </div>
          </div>
          {!user && (
            <div className="form-group">
              <label className="form-label">Password *</label>
              <input className="form-input" type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="Min 8 characters" />
              <div className="form-hint">Default if blank: Welcome@123</div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>
            {loading ? <div className="spinner" style={{borderColor:'rgba(255,255,255,0.3)',borderTopColor:'#fff'}} /> : null}
            {user ? 'Save Changes' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    usersAPI.list({ role: roleFilter }).then(r => setUsers(r.data.users)).finally(() => setLoading(false));
  }, [roleFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="animate-in" style={{ display:'flex', flexDirection:'column', gap:24 }}>
      {/* Tab Switcher */}
      <div className="tab-container">
        <button className={`tab-btn ${tab==='users'?'active':''}`} onClick={() => setTab('users')}>
          <Users size={18} /> Team Members
        </button>
        <button className={`tab-btn ${tab==='permissions'?'active':''}`} onClick={() => setTab('permissions')}>
          <Shield size={18} /> Role Permissions
        </button>
      </div>

      {tab === 'users' ? (
        <>
          {/* Toolbar */}
          <div style={{ display:'flex', gap:16, alignItems:'center', flexWrap:'wrap' }}>
            <div className="search-bar" style={{ flex:1, minWidth:260, height:42 }}>
              <Search size={16} color="var(--text-muted)" />
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name or email..." />
            </div>
            <div className="filter-bar">
              {['','admin','supervisor','technician'].map(r => (
                <button key={r} className={`filter-pill ${roleFilter===r?'active':''}`}
                  style={{ height:38, padding:'0 16px' }}
                  onClick={() => setRoleFilter(r)}>
                  {r || 'All Roles'}
                </button>
              ))}
            </div>
            <button className="btn btn-primary" style={{ height:42, padding:'0 20px' }}
              onClick={() => { setSelected(null); setModal('form'); }}>
              <Plus size={18} /> New User
            </button>
          </div>

          {/* Users Grid */}
          {loading ? (
            <div className="grid-auto">{[1,2,3,4,5,6].map(i => <div key={i} className="card" style={{padding:20, minHeight:200}}><div className="skeleton" style={{height:'100%'}} /></div>)}</div>
          ) : filtered.length === 0 ? (
            <div className="card" style={{ borderStyle:'dashed' }}><div className="empty-state"><Users size={48} className="empty-icon" /><div className="empty-title">No team members found</div><div className="empty-sub">Try adjusting your search or filters</div></div></div>
          ) : (
            <div className="grid-auto animate-in">
              {filtered.map((u, i) => {
                const RoleIcon = ROLE_ICONS[u.role] || Users;
                const rc = ROLE_COLORS[u.role] || '#94A3B8';
                return (
                  <div key={u.id} className="card stagger-in" style={{ display:'flex', flexDirection:'column', animationDelay:`${i*40}ms` }}>
                    <div style={{ padding:'24px 24px 0', display:'flex', gap:16, alignItems:'center' }}>
                      <div className="avatar avatar-lg shadow-sm" style={{ background:getColor(u.name), border:'4px solid var(--bg-card)' }}>
                        {getInitials(u.name)}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:16, fontWeight:800, color:'var(--text)', letterSpacing:'-0.01em' }}>{u.name}</div>
                        <div style={{ fontSize:13, color:'var(--text-muted)', marginTop:2 }} className="truncate">{u.email}</div>
                      </div>
                    </div>

                    <div style={{ padding:'20px 24px', flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
                        <div style={{
                          display:'inline-flex', alignItems:'center', gap:6,
                          padding:'4px 12px', borderRadius:'var(--r-full)',
                          background:`${rc}12`, color:rc, fontSize:12, fontWeight:700
                        }}>
                          <RoleIcon size={12} /> {u.role.toUpperCase()}
                        </div>
                        {!u.is_active && <span className="badge badge-cancelled">Inactive</span>}
                      </div>

                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                        {[
                          { l:'Department', v:u.department||'—' },
                          { l:'Employee ID', v:u.employee_id||'—' },
                        ].map(({ l, v }) => (
                          <div key={l} style={{ background:'var(--bg-muted)40', borderRadius:'var(--r-md)', padding:'10px 12px', border:'1px solid var(--border)50' }}>
                            <div style={{ fontSize:10, color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>{l}</div>
                            <div style={{ fontSize:13, fontWeight:700, color:'var(--text)', marginTop:2 }} className="truncate">{v}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {['super_admin', 'admin'].includes(me?.role) && (
                      <div style={{ padding:'16px 24px', borderTop:'1px solid var(--divider)', background:'var(--bg-muted)20' }}>
                        <button className="btn btn-ghost btn-sm" style={{ width:'100%', justifyContent:'center', borderRadius:'var(--r-md)' }}
                          onClick={() => { setSelected(u); setModal('form'); }}>
                          <Edit2 size={13} /> Edit Profile
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <PermissionMatrix />
      )}

      {modal === 'form' && (
        <UserModal user={selected} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}
